/**
 * QA 2026-10-01 F-08 / F-22 — the one place request limits are defined.
 *
 * Before this module every route typed its own numbers (or none): an
 * `employeeCount` of 99999999999 reached an int4 column and came back as a
 * 500 with the SQL in the body, and a 630-character Quick log note was stored
 * even though the form stops at 500. Every Fastify route schema and every
 * shared Zod input schema takes its bounds from here, and
 * `packages/backend/src/__tests__/request-bounds.test.ts` fails the build when
 * a request number, string or array in `openapi.json` has no upper bound.
 *
 * Rules for changing a limit:
 *   - Never set a server limit below a limit the UI already enforces (the user
 *     would type a value the form accepts and the server rejects).
 *   - Integer columns are int4 in Postgres; nothing that lands in one may
 *     exceed `PG_INT_MAX`.
 */
// ─── Numeric ────────────────────────────────────────────────────────────────
/** Largest value a Postgres `integer` (int4) column holds. */
export const PG_INT_MAX = 2_147_483_647;
/** Money is integer cents in int4 columns: $20,000,000.00. */
export const MONEY_CENTS_MAX = 2_000_000_000;
/** Employees at one company. Larger than any real employer. */
export const EMPLOYEE_COUNT_MAX = 10_000_000;
/** Day counts (alerting thresholds, expiry windows): ten years. */
export const DAYS_MAX = 3650;
/** One activity's duration: a full day. */
export const DURATION_MINUTES_MAX = 1440;
/** A rep's weekly capacity target. */
export const CAPACITY_MAX = 1000;
/** Deepest list page offset any screen requests. */
export const OFFSET_MAX = 100_000;
/** Percentages (discounts). */
export const PCT_MAX = 100;
/** Units on one quote line. */
export const QUANTITY_MAX = 10_000;
/** Ordering priority on admin-managed rule lists. */
export const PRIORITY_MAX = 1000;
/** Products in one bundle. */
export const BUNDLE_PRODUCT_COUNT_MAX = 20;
/** Data rows in one CSV lead import (also the row index ceiling). */
export const IMPORT_ROWS_MAX = 1000;
// ─── Text ───────────────────────────────────────────────────────────────────
/**
 * Character limits for request strings. Matched to the CRM's existing form
 * limits where one exists (Quick log note 500, transition reason 500, meeting
 * title 200, contact notes 5,000, ZIP input 20, email body 100,000).
 */
export const TEXT_LIMITS = {
    name: 200,
    title: 200,
    jobTitle: 120,
    email: 254,
    phoneInput: 30,
    shortText: 200,
    timezone: 64,
    reason: 500,
    briefNote: 500,
    /** Rep-facing justifications and approval decisions. */
    justification: 2000,
    /** Cover note on a proposal email. */
    coverNote: 5000,
    /** Contact notes (the contact dialog allows 5,000). */
    contactNotes: 5000,
    notes: 10_000,
    longText: 100_000,
    tag: 50,
    tagsMax: 50,
    url: 2048,
    id: 128,
    /** Opaque bearer tokens in a path (customer portal, intake, proposal links). */
    token: 512,
    /** An ISO 8601 date or timestamp sent as a plain string. */
    isoDate: 64,
    /** Industry label. */
    industry: 100,
    /** A DNS name (RFC 1035: 253 characters). */
    domain: 253,
    stateCode: 2,
    /** Raw state input before normalization ("Texas", " tx "). */
    stateInput: 64,
    /** Raw ZIP input before normalization; the account form allows 20. */
    postal: 20,
    /** Free-text search boxes. */
    search: 200,
    /** Email subject lines. */
    subject: 200,
    /** Ids, emails or labels in one request list. */
    listMax: 100,
};
/**
 * Cut server-built text (auto-logged activity notes) to a limit instead of
 * rejecting it, so an automatic write can never fail on length. Ends with an
 * ellipsis when cut.
 */
export function truncateText(text, maxLength) {
    if (text.length <= maxLength)
        return text;
    return `${text.slice(0, Math.max(0, maxLength - 1)).trimEnd()}…`;
}
// ─── JSON-schema fragments for Fastify route schemas ────────────────────────
//
// Plain `as const` objects so they drop straight into a route's `schema`
// block and into `openapi.json`. Spread them, never mutate them.
/** Optimistic-concurrency version (`expectedVersion`, `expectedCurrentVersion`). */
export const versionSchema = { type: 'integer', minimum: 1, maximum: PG_INT_MAX };
/** Integer money in cents. */
export const moneyCentsSchema = { type: 'integer', minimum: 0, maximum: MONEY_CENTS_MAX };
/** Signed integer money in cents (credits and adjustments that may be negative). */
export const signedMoneyCentsSchema = {
    type: 'integer',
    minimum: -MONEY_CENTS_MAX,
    maximum: MONEY_CENTS_MAX,
};
/** A whole-number percentage 0..100. */
export const pctSchema = { type: 'integer', minimum: 0, maximum: PCT_MAX };
/** A percentage 0..100 that may carry decimals (e.g. 12.5). */
export const fractionalPctSchema = { type: 'number', minimum: 0, maximum: PCT_MAX };
/** List page offset. */
export const offsetSchema = { type: 'integer', minimum: 0, maximum: OFFSET_MAX };
/** Employee head count. */
export const employeeCountSchema = { type: 'integer', minimum: 0, maximum: EMPLOYEE_COUNT_MAX };
/** A record id in a body, query or path. */
export const idSchema = { type: 'string', maxLength: TEXT_LIMITS.id };
/** An opaque bearer token in a path. */
export const tokenSchema = { type: 'string', maxLength: TEXT_LIMITS.token };
/** An email address sent as a plain string (format is checked by the handler). */
export const emailTextSchema = { type: 'string', maxLength: TEXT_LIMITS.email };
/** An ISO date or timestamp sent as a plain string. */
export const isoDateTextSchema = { type: 'string', maxLength: TEXT_LIMITS.isoDate };
/** List page size with the route's own ceiling. */
export function limitSchema(max) {
    return { type: 'integer', minimum: 1, maximum: max };
}
/** A bounded integer. */
export function intSchema(minimum, maximum) {
    return { type: 'integer', minimum, maximum };
}
export function textSchema(maxLength, opts) {
    return opts?.minLength !== undefined
        ? { type: 'string', minLength: opts.minLength, maxLength }
        : { type: 'string', maxLength };
}
/** A request string that may also be `null` (PATCH: null clears the field). */
export function nullableTextSchema(maxLength) {
    return { type: ['string', 'null'], maxLength };
}
/** A bounded list of bounded strings (tags and the like). */
export function stringArraySchema(maxItems, itemMaxLength) {
    return { type: 'array', maxItems, items: { type: 'string', maxLength: itemMaxLength } };
}
/** Account and activity tags: 50 tags of 50 characters. */
export const tagsSchema = stringArraySchema(TEXT_LIMITS.tagsMax, TEXT_LIMITS.tag);
//# sourceMappingURL=limits.js.map